import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { toast } from 'sonner';
import { HeartHandshake, CheckCircle2, Languages, Globe } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  ['strategique', 'Sponsor stratégique'], ['territorial', 'Sponsor territorial'],
  ['filiere', 'Sponsor de filière'], ['projet', 'Sponsor de projet'],
  ['competences', 'Sponsor en compétences'], ['nature', 'Sponsor en nature'],
  ['media', 'Sponsor média'], ['institutionnel', 'Partenaire institutionnel'],
];
const CONTRIBUTIONS = ['financier', 'materiel', 'technique', 'logistique', 'numerique', 'competences', 'media'];

const COUNTRIES = [
  ['fr', 'France'], ['gp', 'Guadeloupe'], ['mq', 'Martinique'], ['gf', 'Guyane'],
  ['re', 'La Réunion'], ['yt', 'Mayotte'], ['mf', 'Saint-Martin'], ['bl', 'Saint-Barthélemy'],
  ['pm', 'Saint-Pierre-et-Miquelon'], ['pf', 'Polynésie française'], ['nc', 'Nouvelle-Calédonie'],
  ['wf', 'Wallis-et-Futuna'], ['be', 'Belgique'], ['ch', 'Suisse'], ['lu', 'Luxembourg'],
  ['ca', 'Canada'], ['es', 'Espagne'], ['pt', 'Portugal'], ['it', 'Italie'], ['de', 'Allemagne'],
  ['gb', 'Royaume-Uni'], ['us', 'États-Unis'], ['ma', 'Maroc'], ['sn', 'Sénégal'],
  ['ci', "Côte d'Ivoire"], ['', 'Autre'],
];

const T = {
  fr: {
    badge: 'Devenir sponsor',
    title: 'Soutenir un territoire, une filière ou un projet concret',
    subtitle: "Ce formulaire vous permet de proposer un soutien financier, matériel ou en compétences. Les contributions retenues font l'objet d'une convention précisant les actions soutenues, les contreparties de visibilité, la durée et les modalités de suivi.",
    operational: "Vous souhaitez proposer une coopération autour d'une activité ou d'un service ?",
    operational_link: "Présentez votre demande à l'équipe.",
    raison: 'Raison sociale', nom_com: 'Nom commercial', forme: 'Forme juridique', siren: 'SIREN',
    pays: 'Pays', secteur: "Secteur d'activité", site: 'Site internet',
    contact: 'Nom du contact', fonction: 'Fonction', email: 'Email professionnel', tel: 'Téléphone',
    categorie: 'Catégorie de sponsoring', contribution: 'Type de contribution',
    territoires: 'Territoire(s) (séparés par des virgules)', branches: "Branche(s) O'SCOP (virgules)",
    enveloppe: 'Enveloppe indicative', duree: 'Durée envisagée', message: 'Message', choose: 'Choisir…',
    privacy: "J'ai pris connaissance de la politique de confidentialité et j'accepte le traitement de mes données pour l'étude de cette demande.",
    marketing: "J'accepte de recevoir les informations et opportunités de partenariat d'OBJECTIF SCOP OUTREMER.",
    submit: 'Déposer ma candidature', submitting: 'Envoi…',
    disclaimer: "Le dépôt d'une candidature ne vaut ni acceptation du partenariat, ni adhésion à la coopérative, ni admission au capital social.",
    success_title: 'Candidature bien reçue',
    success_text: "Merci ! Notre équipe étudiera votre proposition de partenariat et reviendra vers vous. Un accusé de réception vous a été envoyé par email.",
    err_privacy: 'Veuillez accepter la politique de confidentialité.',
    err_required: 'Raison sociale, nom du contact et email sont requis.',
    sent: 'Candidature envoyée !',
  },
  en: {
    badge: 'Become a sponsor',
    title: 'Support a territory, a sector or a concrete project',
    subtitle: 'Use this form to propose financial, material or skills support. Accepted contributions are formalised in an agreement setting out the supported actions, visibility arrangements, duration and monitoring terms.',
    operational: 'Would you like to propose cooperation on an activity or a service?',
    operational_link: 'Contact the team with your proposal.',
    raison: 'Legal name', nom_com: 'Trade name', forme: 'Legal form', siren: 'Company no.',
    pays: 'Country', secteur: 'Business sector', site: 'Website',
    contact: 'Contact name', fonction: 'Role', email: 'Professional email', tel: 'Phone',
    categorie: 'Sponsoring category', contribution: 'Contribution type',
    territoires: 'Territory(ies) (comma separated)', branches: "O'SCOP branch(es) (comma separated)",
    enveloppe: 'Indicative budget', duree: 'Planned duration', message: 'Message', choose: 'Choose…',
    privacy: 'I have read the privacy policy and accept the processing of my data for the review of this request.',
    marketing: 'I agree to receive information and partnership opportunities from OBJECTIF SCOP OUTREMER.',
    submit: 'Submit my application', submitting: 'Sending…',
    disclaimer: 'Submitting an application does not constitute acceptance of the partnership, membership of the cooperative, or admission to the share capital.',
    success_title: 'Application received',
    success_text: 'Thank you! Our team will review your partnership proposal and get back to you. A confirmation has been sent to your email.',
    err_privacy: 'Please accept the privacy policy.',
    err_required: 'Legal name, contact name and email are required.',
    sent: 'Application sent!',
  },
  es: {
    badge: 'Conviértete en patrocinador',
    title: 'Apoyar un territorio, un sector o un proyecto concreto',
    subtitle: 'Este formulario permite proponer apoyo financiero, material o en competencias. Las contribuciones aceptadas se formalizan en un convenio que precisa las acciones apoyadas, la visibilidad, la duración y el seguimiento.',
    operational: '¿Desea proponer una cooperación en torno a una actividad o un servicio?',
    operational_link: 'Presente su propuesta al equipo.',
    raison: 'Razón social', nom_com: 'Nombre comercial', forme: 'Forma jurídica', siren: 'N.º de empresa',
    pays: 'País', secteur: 'Sector de actividad', site: 'Sitio web',
    contact: 'Nombre del contacto', fonction: 'Función', email: 'Correo profesional', tel: 'Teléfono',
    categorie: 'Categoría de patrocinio', contribution: 'Tipo de contribución',
    territoires: 'Territorio(s) (separados por comas)', branches: "Rama(s) O'SCOP (separadas por comas)",
    enveloppe: 'Presupuesto indicativo', duree: 'Duración prevista', message: 'Mensaje', choose: 'Elegir…',
    privacy: 'He leído la política de privacidad y acepto el tratamiento de mis datos para el estudio de esta solicitud.',
    marketing: 'Acepto recibir información y oportunidades de alianza de OBJECTIF SCOP OUTREMER.',
    submit: 'Enviar mi candidatura', submitting: 'Enviando…',
    disclaimer: 'La presentación de una candidatura no implica aceptación de la alianza, ni adhesión a la cooperativa, ni admisión al capital social.',
    success_title: 'Candidatura recibida',
    success_text: '¡Gracias! Nuestro equipo estudiará su propuesta de alianza y se pondrá en contacto con usted. Se ha enviado una confirmación a su correo.',
    err_privacy: 'Por favor, acepte la política de privacidad.',
    err_required: 'Razón social, nombre del contacto y correo son obligatorios.',
    sent: '¡Candidatura enviada!',
  },
};
const LANGS = [['fr', 'Français'], ['en', 'English'], ['es', 'Español']];

const DIAL_CODES = [
  ['gp', '+590', 'Guadeloupe'], ['mq', '+596', 'Martinique'], ['gf', '+594', 'Guyane'],
  ['re', '+262', 'La Réunion'], ['fr', '+33', 'France'], ['pm', '+508', 'Saint-Pierre-et-Miquelon'],
  ['pf', '+689', 'Polynésie française'], ['nc', '+687', 'Nouvelle-Calédonie'],
  ['be', '+32', 'Belgique'], ['ch', '+41', 'Suisse'], ['lu', '+352', 'Luxembourg'],
  ['ca', '+1', 'Canada'], ['es', '+34', 'Espagne'], ['pt', '+351', 'Portugal'],
  ['it', '+39', 'Italie'], ['de', '+49', 'Allemagne'], ['gb', '+44', 'Royaume-Uni'],
  ['us', '+1', 'États-Unis'], ['ma', '+212', 'Maroc'], ['sn', '+221', 'Sénégal'],
  ['ci', '+225', "Côte d'Ivoire"],
];

function Flag({ iso2 }) {
  if (!iso2) return <Globe className="h-4 w-5 text-slate-400 shrink-0" />;
  return (
    <img
      src={`https://flagcdn.com/24x18/${iso2}.png`}
      srcSet={`https://flagcdn.com/48x36/${iso2}.png 2x`}
      width={22} height={16} alt="" loading="lazy"
      className="rounded-[2px] shrink-0 object-cover"
    />
  );
}

function FlagSelect({ value, onValueChange, items, renderLabel, testid }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger data-testid={testid} className="bg-slate-950 border-slate-700 text-white">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-slate-900 border-slate-700 text-white max-h-72">
        {items.map((it) => (
          <SelectItem key={it.key} value={it.key} className="text-white focus:bg-slate-800 focus:text-white">
            <span className="flex items-center gap-2"><Flag iso2={it.iso2} />{renderLabel(it)}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function DevenirSponsorPage() {
  const [lang, setLang] = useState('fr');
  const tr = T[lang];
  const [phoneIso, setPhoneIso] = useState('gp');
  const phoneCode = (DIAL_CODES.find((d) => d[0] === phoneIso) || [])[1] || '';
  const [form, setForm] = useState({
    organisation_name: '', legal_name: '', forme_juridique: '', siren: '', pays: 'France',
    website_url: '', secteur_activite: '', contact_name: '', contact_role: '', email: '', phone: '',
    sponsor_category: '', territories: '', branches: '', contribution_type: '', budget_range: '',
    duree: '', message: '',
  });
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!privacy) { toast.error(tr.err_privacy); return; }
    if (!form.organisation_name || !form.contact_name || !form.email) {
      toast.error(tr.err_required); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/sponsors/candidatures`, {
        ...form,
        phone: form.phone ? `${phoneCode} ${form.phone}`.trim() : '',
        territories: form.territories ? form.territories.split(',').map((x) => x.trim()).filter(Boolean) : [],
        branches: form.branches ? form.branches.split(',').map((x) => x.trim()).filter(Boolean) : [],
        privacy_accepted: privacy, marketing_opt_in: marketing,
      });
      setDone(true);
      toast.success(tr.sent);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi.');
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <Navigation />
        <div className="max-w-2xl mx-auto px-4 pt-32 pb-24 text-center" data-testid="candidature-success">
          <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-400 mb-6" />
          <h1 className="text-3xl font-bold">{tr.success_title}</h1>
          <p className="mt-4 text-slate-300">{tr.success_text}</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="devenir-sponsor-page">
      <Navigation />
      <div className="max-w-3xl mx-auto px-4 pt-24 pb-16">
        <BackButton />
        <div className="mt-6 mb-8">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 text-emerald-300 text-sm font-medium">
              <HeartHandshake className="w-4 h-4" /> {tr.badge}
            </div>
            <div className="inline-flex items-center gap-1.5 bg-slate-900 border border-slate-800 rounded-full p-1" data-testid="lang-switcher">
              <Languages className="w-4 h-4 text-slate-400 ml-2" />
              {LANGS.map(([code, label]) => (
                <button key={code} type="button" onClick={() => setLang(code)} data-testid={`lang-${code}`}
                  className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${lang === code ? 'bg-emerald-600 text-white' : 'text-slate-300 hover:text-white'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-4">{tr.title}</h1>
          <p className="mt-4 text-slate-300">{tr.subtitle}</p>
          <p className="mt-4 text-sm text-slate-300">{tr.operational} <Link to="/contact" className="text-emerald-300 hover:text-emerald-200 underline underline-offset-4">{tr.operational_link}</Link></p>
        </div>

        <Card className="bg-slate-900 border-slate-800"><CardContent className="p-6">
          <form onSubmit={submit} className="space-y-5" data-testid="sponsor-application-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={`${tr.raison} *`} testid="app-organisation"><Input required value={form.organisation_name} onChange={(e) => set('organisation_name', e.target.value)} className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500" /></Field>
              <Field label={tr.nom_com}><Input value={form.legal_name} onChange={(e) => set('legal_name', e.target.value)} className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500" /></Field>
              <Field label={tr.forme}><Input value={form.forme_juridique} onChange={(e) => set('forme_juridique', e.target.value)} className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500" /></Field>
              <Field label={tr.siren}><Input value={form.siren} onChange={(e) => set('siren', e.target.value)} className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500" /></Field>
              <Field label={tr.pays}>
                <FlagSelect
                  testid="app-country"
                  value={form.pays}
                  onValueChange={(v) => set('pays', v)}
                  items={COUNTRIES.map(([iso2, name]) => ({ key: name, iso2, name }))}
                  renderLabel={(it) => it.name}
                />
              </Field>
              <Field label={tr.secteur}><Input value={form.secteur_activite} onChange={(e) => set('secteur_activite', e.target.value)} className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500" /></Field>
              <Field label={tr.site}><Input value={form.website_url} onChange={(e) => set('website_url', e.target.value)} placeholder="https://" className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500" /></Field>
            </div>

            <div className="border-t border-slate-800 pt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={`${tr.contact} *`} testid="app-contact"><Input required value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500" /></Field>
              <Field label={tr.fonction}><Input value={form.contact_role} onChange={(e) => set('contact_role', e.target.value)} className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500" /></Field>
              <Field label={`${tr.email} *`} testid="app-email"><Input required type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500" /></Field>
              <Field label={tr.tel}>
                <div className="flex gap-2">
                  <div className="w-32 shrink-0">
                    <FlagSelect
                      testid="app-phone-code"
                      value={phoneIso}
                      onValueChange={setPhoneIso}
                      items={DIAL_CODES.map(([iso2, code]) => ({ key: iso2, iso2, code }))}
                      renderLabel={(it) => it.code}
                    />
                  </div>
                  <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="690 00 00 00" data-testid="app-phone" className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500" />
                </div>
              </Field>
            </div>

            <div className="border-t border-slate-800 pt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={tr.categorie}>
                <select value={form.sponsor_category} onChange={(e) => set('sponsor_category', e.target.value)} data-testid="app-category"
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white">
                  <option value="">{tr.choose}</option>
                  {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              <Field label={tr.contribution}>
                <select value={form.contribution_type} onChange={(e) => set('contribution_type', e.target.value)} data-testid="app-contribution"
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white">
                  <option value="">{tr.choose}</option>
                  {CONTRIBUTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </Field>
              <Field label={tr.territoires}><Input value={form.territories} onChange={(e) => set('territories', e.target.value)} placeholder="Guadeloupe, Martinique" className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500" /></Field>
              <Field label={tr.branches}><Input value={form.branches} onChange={(e) => set('branches', e.target.value)} placeholder="BATI’SCOP, FORMA’SCOP, LOGI’SCOP" className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500" /></Field>
              <Field label={tr.enveloppe}><Input value={form.budget_range} onChange={(e) => set('budget_range', e.target.value)} className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500" /></Field>
              <Field label={tr.duree}><Input value={form.duree} onChange={(e) => set('duree', e.target.value)} className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500" /></Field>
            </div>

            <Field label={tr.message}><Textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={4} className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500" /></Field>

            <div className="space-y-3 border-t border-slate-800 pt-5">
              <label className="flex items-start gap-3 text-sm text-slate-200">
                <Checkbox checked={privacy} onCheckedChange={setPrivacy} data-testid="app-privacy" className="mt-0.5" />
                <span>{tr.privacy}</span>
              </label>
              <label className="flex items-start gap-3 text-sm text-slate-300">
                <Checkbox checked={marketing} onCheckedChange={setMarketing} data-testid="app-marketing" className="mt-0.5" />
                <span>{tr.marketing}</span>
              </label>
            </div>

            <Button type="submit" disabled={submitting} data-testid="app-submit" className="w-full bg-emerald-600 hover:bg-emerald-500 rounded-full">
              {submitting ? tr.submitting : tr.submit}
            </Button>
            <p className="text-xs text-slate-400 text-center">{tr.disclaimer}</p>
          </form>
        </CardContent></Card>
      </div>
      <Footer />
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Label className="text-slate-200 text-sm font-medium">{label}</Label>
      {children}
    </div>
  );
}
